import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  Box,
  Typography,
  Chip,
  Stack
} from '@mui/material';
import {
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Facebook as FacebookIcon,
  X as XIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const ShareButton = ({ 
  url = window.location.href,
  title = document.title,
  description = '',
  hashtags = [],
  onClick,
  size = 'small',
  color = 'text.secondary',
  variant = 'icon' // 'icon' | 'button' | 'fab'
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [emailForm, setEmailForm] = useState({
    to: '',
    subject: `Check out: ${title}`,
    body: `I thought you might find this interesting:\n\n${title}\n${url}\n\n${description}`
  });

  const handleShareClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (onClick) {
      onClick();
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      showSnackbar('Link copied to clipboard!', 'success');
      handleClose();
    } catch (err) {
      showSnackbar('Failed to copy link', 'error');
    }
  };

  const handleEmailDialogOpen = () => {
    setEmailDialogOpen(true);
    handleClose();
  };

  const handleShareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url,
        });
        showSnackbar('Shared successfully!', 'success');
      } catch (err) {
        if (err.name !== 'AbortError') {
          showSnackbar('Failed to share', 'error');
        }
      }
    } else {
      handleCopyLink();
    }
    handleClose();
  };

  const handleSocialShare = (platform) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'X':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}${hashtags.length ? `&hashtags=${hashtags.join(',')}` : ''}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`;
        break;
    //   case 'telegram':
    //     shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
    //     break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const handleEmailShare = () => {
    const mailtoLink = `mailto:${emailForm.to}?subject=${encodeURIComponent(emailForm.subject)}&body=${encodeURIComponent(emailForm.body)}`;
    window.location.href = mailtoLink;
    setEmailDialogOpen(false);
    showSnackbar('Email opened with pre-filled content', 'success');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const shareOptions = [
    {
      label: 'Copy Link',
      icon: <CopyIcon fontSize="small" />,
      onClick: handleCopyLink,
      native: true
    },
    {
      label: 'Share via...',
      icon: <ShareIcon fontSize="small" />,
      onClick: handleShareNative,
      native: true,
      hide: !navigator.share
    },
    {
      label: 'Email',
      icon: <EmailIcon fontSize="small" />,
      onClick: handleEmailDialogOpen
    },
    {
      label: 'X',
      icon: <XIcon fontSize="small" />,
      onClick: () => handleSocialShare('X'),
      color: '#1DA1F2'
    },
    // {
    //   label: 'Facebook',
    //   icon: <FacebookIcon fontSize="small" />,
    //   onClick: () => handleSocialShare('facebook'),
    //   color: '#1877F2'
    // },
    {
      label: 'LinkedIn',
      icon: <LinkedInIcon fontSize="small" />,
      onClick: () => handleSocialShare('linkedin'),
      color: '#0A66C2'
    },
    // {
    //   label: 'WhatsApp',
    //   icon: <WhatsAppIcon fontSize="small" />,
    //   onClick: () => handleSocialShare('whatsapp'),
    //   color: '#25D366'
    // },
    // {
    //   label: 'Telegram',
    //   icon: <TelegramIcon fontSize="small" />,
    //   onClick: () => handleSocialShare('telegram'),
    //   color: '#0088CC'
    // }
  ];

  const renderButton = () => {
    switch (variant) {
      case 'button':
        return (
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={handleShareClick}
            size={size}
            sx={{ color }}
          >
            Share
          </Button>
        );
      case 'fab':
        return (
          <IconButton
            color="primary"
            onClick={handleShareClick}
            aria-label="Share"
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            <ShareIcon />
          </IconButton>
        );

        case 'dropdown':
          return (
           
              <Button
                size={size}
                sx={{ 
                  width: '100%', 
                  justifyContent: 'flex-start',
                
                  // padding: '6px 16px', // Match MenuItem padding
                  // color
                }}
                onClick={handleShareClick}
                aria-label="Share"
                
                
              >
                <ShareIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" ml={2} fontSize="1.4rem" color="text.primary">
                  Share
                </Typography>
              </Button>
           
          );

      default:
        return (
          <Tooltip title="Share">
            <IconButton
              size={size}
              sx={{ color }}
              onClick={handleShareClick}
              aria-label="Share"
            >
              <ShareIcon />
            </IconButton>
         </Tooltip>
        );
    }
  };

  return (
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'contents' }}>
      {renderButton()}

      {/* Share Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {shareOptions
          .filter(option => !option.hide)
          .map((option, index) => (
            <MenuItem 
              key={option.label} 
              onClick={option.onClick}
              sx={{
                ...(option.color && { color: option.color }),
                ...(option.native && { fontWeight: 'medium' })
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                {option.icon}
              </ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          ))}
      </Menu>

      {/* Copy Link Dialog */}
      <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)}>
        <DialogTitle>Copy Link</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={url}
            margin="dense"
            InputProps={{
              readOnly: true,
            }}
            onFocus={(e) => e.target.select()}
          />
          {hashtags.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Suggested hashtags:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {hashtags.map(tag => (
                  <Chip
                    key={tag}
                    label={`#${tag}`}
                    size="small"
                    onClick={() => navigator.clipboard.writeText(`#${tag}`)}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={handleCopyLink}
          >
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Share Dialog */}
      <Dialog 
        open={emailDialogOpen} 
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Share via Email
          <IconButton
            aria-label="close"
            onClick={() => setEmailDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Recipient Email"
              type="email"
              fullWidth
              value={emailForm.to}
              onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
              placeholder="example@email.com"
            />
            <TextField
              label="Subject"
              fullWidth
              value={emailForm.subject}
              onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={6}
              value={emailForm.body}
              onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
            />
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                URL: {url}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            onClick={handleEmailShare}
            disabled={!emailForm.to}
          >
            Open Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          icon={snackbar.severity === 'success' ? <CheckCircleIcon /> : undefined}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </span>
  );
};

// Enhanced version with QR code and embed options
export const AdvancedShareButton = (props) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  return (
    <>
      <ShareButton {...props} onClick={() => setShowAdvanced(true)} />
      
      <Dialog 
        open={showAdvanced} 
        onClose={() => setShowAdvanced(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Share Options</DialogTitle>
        <DialogContent>
          {/* Add QR code generation here */}
          {/* Add embed code generation here */}
          {/* Add social media preview customization */}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Hook version for custom implementations
export const useShare = ({ url, title, description, hashtags }) => {
  const [isSharing, setIsSharing] = useState(false);

  const share = async (method = 'native') => {
    setIsSharing(true);
    
    try {
      switch (method) {
        case 'native':
          if (navigator.share) {
            await navigator.share({ title, text: description, url });
          } else {
            await navigator.clipboard.writeText(url);
          }
          break;
        case 'x':
          window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
          break;
        case 'copy':
          await navigator.clipboard.writeText(url);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Share failed:', error);
    } finally {
      setIsSharing(false);
    }
  };

  return { share, isSharing };
};

export default ShareButton;